import { useState } from "react";
import { DownloadEmailEml, EmlDownloadFailure } from "../EmailDetailAPI";
import { EmailRef } from "../EmailSearchAPI";

interface iEmlDownloadProps {
    /** Which email. Carried as a unit; see contract §2.7. */
    emailRef: EmailRef;
    tenantKey: string;
    tokenFor: (tenantKey: string) => Promise<string>;
}

/**
 * A `Record`, so a new `EmlDownloadFailure` cannot compile until it has copy.
 *
 * `unauthorised` reuses the detail view's own session-expiry sentence: it is
 * the same event, and the operator should read it the same way.
 */
const FAILURE_COPY: Record<EmlDownloadFailure, string> = {
    unauthorised: "Your session has expired. Reload the page to sign in again.",
    forbidden: "You don't have permission to download this email.",
    // Also an outbound email that was never built: there is no file to give.
    notFound: "This email isn't available for download.",
    unreadable: "The stored email could not be read.",
    interrupted: "The download was interrupted. Try again.",
    failed: "The download failed. Try again.",
};

/**
 * Save this email as a `.eml`, for inspection in a mail client.
 *
 * Rendered only inside the detail view, which is itself only reachable from
 * the sys-admin-gated monitor — so the button is shown exactly where the
 * monitor is, and nowhere the API would `403` by design.
 *
 * INDETERMINATE while running: the response carries no `Content-Length`, so
 * there is no percentage to show.
 */
function EmlDownload({ emailRef, tenantKey, tokenFor }: iEmlDownloadProps) {
    const [downloading, setDownloading] = useState(false);
    const [failure, setFailure] = useState<EmlDownloadFailure | undefined>(undefined);

    async function download() {
        setDownloading(true);
        setFailure(undefined);
        try {
            let token: string;
            try {
                token = await tokenFor(tenantKey);
            } catch (error) {
                // `getTenantAuth` throws bare `Error("401")` strings; see
                // `stateForMintError` in useEmailDetail.
                setFailure(error instanceof Error && error.message === "401" ? "unauthorised" : "failed");
                return;
            }

            const result = await DownloadEmailEml(token, emailRef);
            if (result.kind === "failed") {
                setFailure(result.reason);
            }
        } finally {
            setDownloading(false);
        }
    }

    return (
        <div className="mp-detail-eml">
            <button
                type="button"
                className="g-btn btn-default"
                onClick={download}
                disabled={downloading}
                aria-busy={downloading}
            >
                {downloading ? "Downloading…" : "Download .eml"}
            </button>
            {failure ? (
                <p
                    role="alert"
                    className="mp-detail-eml-error"
                >
                    {FAILURE_COPY[failure]}
                </p>
            ) : (
                <p className="mp-action-why">
                    {emailRef.direction === "Inbound"
                        ? "The message as it was received."
                        : "The message as it was built for sending."}
                </p>
            )}
        </div>
    );
}

export default EmlDownload;
